import { useState } from "react";
import { Clock, ExternalLink, Link2, UserPlus, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ROTULO_APOIO } from "@/lib/tipoProfissional";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import type { RouterOutput } from "@/types";

// ============================================================
// 25/09/2026 — Acessos sem ficha (topo de Colaboradores)
// ============================================================
// Pedido do Bryan (24/09): quem entrava no RH não aparecia para configurar —
// o login nascia em `users`, mas ninguém criava a ficha em `profissionais`.
// Desde 25/09 a ficha nasce sozinha quando a central (ou o admin) libera
// veterinário, auxiliar ou coordenação de escala. Este bloco mostra o que
// sobra para decidir à mão:
//   • quem ficou sem ficha (inclusive quando parece ser alguém já cadastrado —
//     aí a ficha automática não nasce, para não duplicar a pessoa);
//   • gestão (admin/financeiro) sem ficha, fora da contagem;
//   • logins repetidos de quem já tem ficha (mesmo e-mail em duas linhas);
//   • pedidos que ainda estão na central do ERP.

const URL_CENTRAL = "https://app.vetcentermiguel.com/usuarios";

type Dados = RouterOutput["colaboradores"]["acessosSemFicha"];
type Item = Dados["semFicha"][number];

const ROTULO_PAPEL: Record<string, string> = {
  veterinario: "Veterinário",
  auxiliar: "Auxiliar",
  escala: "Coordenação de escala",
  admin: "Admin",
  financeiro: "Financeiro",
  pendente: "Pendente",
};

const ROTULO_SETOR: Record<string, string> = {
  clinica: "Clínica",
  agro: "Agro",
  bt: "Banho & Tosa",
};

function quando(iso: string | null): string {
  if (!iso) return "nunca entrou";
  return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function SugestaoVinculo({ item }: { item: Item }) {
  const s = item.sugestao;
  if (s === null) {
    return <span className="text-slate-400">clínica sem resposta agora — sem sugestão de vínculo</span>;
  }
  if (!s.vcmProfissionalId) {
    return <span className="text-slate-500">e-mail não bate com nenhuma ficha da clínica</span>;
  }
  return (
    <span className={s.emailBate ? "text-emerald-700" : "text-amber-700"}>
      {s.emailBate ? "e-mail bate com a ficha da clínica" : "e-mail não bate; nome parecido com a ficha da clínica"}{" "}
      "{s.nome}" (#{s.vcmProfissionalId})
      {s.jaLigadaA ? ` — já ligada a "${s.jaLigadaA.nome}" no RH` : ""}
    </span>
  );
}

function FormCriarFicha({ item, onFechar }: { item: Item; onFechar: () => void }) {
  const utils = trpc.useUtils();
  const s = item.sugestao;
  const podeVincular = !!s?.vcmProfissionalId && !s.jaLigadaA;
  const [tipo, setTipo] = useState<"veterinario" | "auxiliar" | "apoio">(item.tipoSugerido);
  const [setor, setSetor] = useState<"clinica" | "agro" | "bt">("clinica");
  // E-mail igual vem marcado; nome parecido o admin marca se conferir.
  const [vincular, setVincular] = useState<boolean>(podeVincular && !!s?.emailBate);
  const [erro, setErro] = useState<string | null>(null);

  const criar = trpc.colaboradores.criarFichaDeAcesso.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.colaboradores.acessosSemFicha.invalidate(),
        utils.colaboradores.listar.invalidate(),
      ]);
      onFechar();
    },
    onError: (e) => setErro(e.message),
  });

  return (
    <div className="mt-3 space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-wrap gap-3">
        <label className="text-xs font-medium text-slate-600">
          Tipo
          <select
            className="mt-1 flex h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
            value={tipo}
            onChange={(e) => setTipo(e.target.value as "veterinario" | "auxiliar" | "apoio")}
          >
            <option value="veterinario">Veterinário</option>
            <option value="auxiliar">Auxiliar</option>
            <option value="apoio">{ROTULO_APOIO}</option>
          </select>
        </label>
        <label className="text-xs font-medium text-slate-600">
          Setor
          <select
            className="mt-1 flex h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
            value={setor}
            onChange={(e) => setSetor(e.target.value as "clinica" | "agro" | "bt")}
          >
            {(["clinica", "agro", "bt"] as const).map((v) => (
              <option key={v} value={v}>
                {ROTULO_SETOR[v]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {podeVincular && s ? (
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="mt-1"
            checked={vincular}
            onChange={(e) => setVincular(e.target.checked)}
          />
          <span>
            Ligar à ficha da clínica "{s.nome}" (#{s.vcmProfissionalId}) —{" "}
            {s.emailBate ? "e-mail igual" : "sugestão por nome, confira antes de marcar"}
          </span>
        </label>
      ) : (
        <p className="text-xs text-slate-500">
          A ficha nasce sem vínculo com a clínica — dá para ligar depois em Configurações › Vínculos.
        </p>
      )}

      {erro && <p className="text-sm text-red-700">{erro}</p>}

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={criar.isPending}
          onClick={() => {
            setErro(null);
            criar.mutate({
              userId: item.userId,
              tipo,
              setor,
              vcmProfissionalId: vincular && podeVincular && s ? s.vcmProfissionalId : null,
            });
          }}
        >
          {criar.isPending ? "Criando…" : `Criar ficha "${item.nomeFicha}"`}
        </Button>
        <Button size="sm" variant="outline" onClick={onFechar} disabled={criar.isPending}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

function LinhaSemFicha({ item, isAdmin, gestao }: { item: Item; isAdmin: boolean; gestao: boolean }) {
  const [aberto, setAberto] = useState(false);
  const utils = trpc.useUtils();
  const ligar = trpc.profissionais.vincularUsuario.useMutation({
    onSuccess: () => {
      utils.colaboradores.acessosSemFicha.invalidate();
      utils.colaboradores.listar.invalidate();
    },
    onError: (e) => alert(e.message),
  });
  const p = item.parecida;

  return (
    <li className="rounded-md border border-slate-200 bg-white p-3 text-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-0.5">
          <p className="font-medium text-slate-900">
            {item.nome}
            <Badge variant="outline" className="ml-2">
              {ROTULO_PAPEL[item.papel] ?? item.papel}
            </Badge>
          </p>
          <p className="text-xs text-slate-500">
            {item.email} · último acesso {quando(item.ultimoAcesso)}
          </p>
          <p className="text-xs">
            <SugestaoVinculo item={item} />
          </p>
          {gestao && (
            <p className="text-xs text-slate-500">
              Admin e financeiro não ganham ficha sozinhos — crie só se a pessoa também trabalha na escala.
            </p>
          )}
          {p && (
            <p className="text-xs text-amber-800">
              Atenção: {p.motivo}
              {p.ativo ? "" : " (ficha inativa)"}.{" "}
              {gestao
                ? "Confira se não é a mesma pessoa antes de criar."
                : "A ficha automática não foi criada para não duplicar a pessoa."}
            </p>
          )}
        </div>

        {isAdmin ? (
          !aberto && (
            <div className="flex flex-wrap gap-2">
              {p?.semLogin && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={ligar.isPending}
                  onClick={() => {
                    if (
                      confirm(
                        `Ligar o login ${item.email} à ficha "${p.nome}" (#${p.profissionalId})?\n` +
                          "A pessoa passa a ver os turnos e o extrato dessa ficha.",
                      )
                    ) {
                      ligar.mutate({ id: p.profissionalId, userId: item.userId });
                    }
                  }}
                >
                  <Link2 className="mr-1.5 h-4 w-4" />
                  Ligar a "{p.nome}"
                </Button>
              )}
              <Button size="sm" variant={p || gestao ? "outline" : "default"} onClick={() => setAberto(true)}>
                <UserPlus className="mr-1.5 h-4 w-4" />
                {p ? "Criar ficha mesmo assim" : "Criar ficha"}
              </Button>
            </div>
          )
        ) : (
          <span className="text-xs text-slate-400">só o administrador cria a ficha</span>
        )}
      </div>
      {aberto && <FormCriarFicha item={item} onFechar={() => setAberto(false)} />}
    </li>
  );
}

export function AcessosSemFicha() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const q = trpc.colaboradores.acessosSemFicha.useQuery();
  const d = q.data;
  if (!d) return null;

  const t = d.totais;
  if (t.semFicha + t.gestao + t.contasRepetidas + t.aguardandoCentral === 0) return null;

  return (
    <Card className="border-amber-200">
      <CardContent className="space-y-4 py-4">
        <div className="space-y-1">
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
            <Users className="h-4 w-4 text-amber-600" />
            Acessos sem ficha ({t.semFicha})
          </h2>
          <p className="text-xs text-slate-500">
            Quem tem login no RH e não tem ficha não aparece na lista abaixo, não entra na escala e não tem
            extrato. Desde 25/09 a ficha nasce sozinha quando o acesso de veterinário, auxiliar ou
            coordenação de escala é liberado; aqui fica o que precisa de decisão.
          </p>
          {d.erroClinica && (
            <p className="text-xs text-amber-800">
              A clínica não respondeu ({d.erroClinica}) — sem sugestão de vínculo nesta carga.
            </p>
          )}
        </div>

        {d.semFicha.length > 0 ? (
          <ul className="space-y-2">
            {d.semFicha.map((i) => (
              <LinhaSemFicha key={i.userId} item={i} isAdmin={isAdmin} gestao={false} />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">Ninguém da escala está sem ficha.</p>
        )}

        {d.gestao.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Gestão sem ficha ({d.gestao.length}) — fora da contagem
            </p>
            <ul className="space-y-2">
              {d.gestao.map((i) => (
                <LinhaSemFicha key={i.userId} item={i} isAdmin={isAdmin} gestao />
              ))}
            </ul>
          </div>
        )}

        {d.contasRepetidas.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Logins repetidos ({d.contasRepetidas.length}) — a pessoa já tem ficha
            </p>
            <ul className="space-y-2">
              {d.contasRepetidas.map((i) => (
                <li key={i.userId} className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                  <span className="font-medium text-slate-800">{i.nome}</span> · {i.email} · último acesso{" "}
                  {quando(i.ultimoAcesso)}
                  <br />
                  Mesmo e-mail da ficha "{i.fichaDaPessoa?.nome}" (#{i.fichaDaPessoa?.profissionalId}), que está
                  ligada a outra linha de login (#{i.fichaDaPessoa?.userId}). Não precisa de ficha nova — se a pessoa
                  não achar o próprio extrato, é porque entra por esta linha (#{i.userId}): unificar os dois logins é
                  ajuste de suporte.
                </li>
              ))}
            </ul>
          </div>
        )}

        {d.aguardandoCentral.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Aguardando a central ({d.aguardandoCentral.length})
            </p>
            <ul className="space-y-2">
              {d.aguardandoCentral.map((i) => (
                <li
                  key={i.userId}
                  className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-slate-200 bg-white p-3 text-sm"
                >
                  <div>
                    <p className="font-medium text-slate-900">{i.nome}</p>
                    <p className="text-xs text-slate-500">
                      {i.email} · pediu acesso em {quando(i.criadoEm)}
                    </p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-amber-800">
                      <Clock className="h-3.5 w-3.5" />
                      aguardando aprovação na central do ERP (Usuários → Acessos centrais)
                    </p>
                  </div>
                  <a
                    href={URL_CENTRAL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline"
                  >
                    Abrir a central <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
